import {
  Component,
  computed,
  ElementRef,
  inject,
  signal,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltip } from '@angular/material/tooltip';
import { UserIdToUserNamePipe } from '@app/features/users/user-id-to-user-name.pipe';
import { userRoleLabel } from '@app/features/users/user-role-labels';
import { realmRoles, UserRole } from '@app/features/users/user.model';
import { SaveButton } from '@app/shared/buttons/save-button/save-button';
import {
  LucideBold,
  LucideCircleCheck,
  LucideClock,
  LucideItalic,
  LucideLink,
  LucideList,
  LucideTriangleAlert,
} from '@lucide/angular';
import moment, { Moment } from 'moment';
import { map } from 'rxjs';
import { AnomalyBanner } from '../anomaly-banner/anomaly-banner';
import {
  AnomalyAction,
  AnomalyApiError,
  anomalyErrorMessage,
  audienceSentence,
} from '../anomaly-presentation';
import { Anomaly, AnomalyRequest, AnomalyStatus, BannerAnomaly } from '../anomaly.model';
import { AnomalyService } from '../anomaly.service';
import { applyMarkdown, MarkdownFormat } from '../markdown-editing';
import { formatDateTime, parisNow } from '../paris-time';
import {
  ALL_ROLES,
  atLeastOneRole,
  bounds,
  nextRoleSelection,
  realRoles,
  RoleOption,
  validPeriod,
  withAllRoles,
} from '../targeting';

export interface AnomalyDialogData {
  /** L'anomalie à modifier, ou à consulter si elle est archivée ; absente pour en signaler une. */
  anomaly?: Anomaly;
}

/**
 * Signaler, modifier, résoudre ou archiver une anomalie (RG-anomalie-saisie-par-la-direction), sur
 * le modèle du dialogue de la grille de salaires.
 */
@Component({
  selector: 'app-anomaly-dialog',
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatDatepickerModule,
    MatDialogActions,
    MatDialogContent,
    MatDialogTitle,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatTabsModule,
    MatTooltip,
    SaveButton,
    AnomalyBanner,
    UserIdToUserNamePipe,
    LucideBold,
    LucideItalic,
    LucideList,
    LucideLink,
    LucideClock,
    LucideTriangleAlert,
    LucideCircleCheck,
  ],
  templateUrl: './anomaly-dialog.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./anomaly-dialog.scss'],
})
export class AnomalyDialog {
  private readonly dialogRef = inject(MatDialogRef<AnomalyDialog>);
  private readonly anomalyService = inject(AnomalyService);
  protected readonly anomaly = inject<AnomalyDialogData>(MAT_DIALOG_DATA).anomaly;
  protected readonly readOnly = !!this.anomaly?.archived;

  protected readonly roles = realmRoles;
  protected readonly ALL_ROLES = ALL_ROLES;
  protected readonly roleLabel = userRoleLabel;
  protected readonly formatDateTime = formatDateTime;

  readonly form = new FormGroup(
    {
      title: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(200)],
      }),
      status: new FormControl<AnomalyStatus>('IN_PROGRESS', { nonNullable: true }),
      content: new FormControl('', { nonNullable: true, validators: Validators.required }),
      targetRoles: new FormControl<RoleOption[]>([], {
        nonNullable: true,
        validators: atLeastOneRole,
      }),
      startDate: new FormControl<Moment | null>(null),
      startTime: new FormControl<string | null>(null),
      endDate: new FormControl<Moment | null>(null),
      endTime: new FormControl<string | null>(null),
    },
    { validators: validPeriod },
  );

  private readonly value = toSignal(
    this.form.valueChanges.pipe(map(() => this.form.getRawValue())),
    {
      initialValue: this.form.getRawValue(),
    },
  );
  private readonly valid = toSignal(this.form.statusChanges.pipe(map(() => this.form.valid)), {
    initialValue: this.form.valid,
  });

  protected readonly canSave = computed(() => this.valid() && !this.readOnly);
  protected readonly confirmArchive = signal(false);
  /** Le refus de l'API, dit en mots. */
  protected readonly error = signal<string | null>(null);

  protected readonly rolesHint = computed(
    () => `${realRoles(this.value().targetRoles).length} rôles sur ${realmRoles.length}`,
  );
  protected readonly rolesTrigger = computed(() => {
    const roles = realRoles(this.value().targetRoles);
    return roles.length === realmRoles.length
      ? 'Tous les rôles'
      : roles.map((role) => userRoleLabel(role)).join(', ');
  });

  protected readonly audience = computed(() => {
    const value = this.value();
    return audienceSentence(
      {
        status: value.status,
        targetRoles: realRoles(value.targetRoles),
        ...bounds(value),
        resolvedAt: this.anomaly?.resolvedAt,
      },
      parisNow(),
    );
  });

  /** Le bandeau tel que les utilisateurs le verront. */
  protected readonly preview = computed<BannerAnomaly>(() => {
    const value = this.value();
    const { startDate, startTime } = bounds(value);
    const now = parisNow();
    return {
      title: value.title || 'Titre de l’anomalie',
      content: value.content,
      status: value.status,
      displayedFrom: startDate
        ? `${startDate}T${startTime ?? '00:00'}:00`
        : (this.anomaly?.displayedFrom ?? now),
      resolvedAt: value.status === 'RESOLVED' ? (this.anomaly?.resolvedAt ?? now) : null,
      updatedAt: null,
    };
  });

  private readonly saveButton = viewChild(SaveButton);
  private readonly contentArea = viewChild<ElementRef<HTMLTextAreaElement>>('contentArea');
  private previousRoles: RoleOption[] = [];

  constructor() {
    if (this.anomaly) {
      const roles = withAllRoles(this.anomaly.targetRoles);
      this.form.setValue({
        title: this.anomaly.title,
        status: this.anomaly.status,
        content: this.anomaly.content,
        targetRoles: roles,
        startDate: this.anomaly.startDate ? moment.utc(this.anomaly.startDate) : null,
        startTime: this.anomaly.startTime?.slice(0, 5) ?? null,
        endDate: this.anomaly.endDate ? moment.utc(this.anomaly.endDate) : null,
        endTime: this.anomaly.endTime?.slice(0, 5) ?? null,
      });
      this.previousRoles = roles;
    }
    if (this.readOnly) {
      this.form.disable();
    }
  }

  /** Les rôles cibles retenus, sans l'entrée « Tous les rôles ». */
  selectedRoles(): UserRole[] {
    return realRoles(this.form.controls.targetRoles.value);
  }

  /** « Tous les rôles » coche ou décoche tout ; retirer un rôle le décoche (RG-anomalie-ciblage-par-role). */
  protected onRolesChange(selected: RoleOption[]): void {
    const next = nextRoleSelection(this.previousRoles, selected);
    this.previousRoles = next;
    this.form.controls.targetRoles.setValue(next);
  }

  protected format(format: MarkdownFormat): void {
    const textarea = this.contentArea()?.nativeElement;
    if (!textarea) return;
    const edit = applyMarkdown(
      textarea.value,
      textarea.selectionStart,
      textarea.selectionEnd,
      format,
    );
    this.form.controls.content.setValue(edit.value);
    this.form.controls.content.markAsDirty();
    textarea.value = edit.value;
    textarea.focus();
    textarea.setSelectionRange(edit.selectionStart, edit.selectionEnd);
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    if (!this.form.valid || this.readOnly) return;
    const value = this.form.getRawValue();
    const request: AnomalyRequest = {
      title: value.title.trim(),
      content: value.content,
      status: value.status,
      targetRoles: realRoles(value.targetRoles),
      ...bounds(value),
    };
    this.error.set(null);
    this.saveButton()?.startSaving();
    const save$ = this.anomaly
      ? this.anomalyService.update(this.anomaly.id, request)
      : this.anomalyService.create(request);
    save$.subscribe({
      next: (anomaly) => {
        this.saveButton()?.stopSaving(true);
        this.dialogRef.close(anomaly);
      },
      error: (error) => {
        this.saveButton()?.stopSaving(false);
        this.showError(error, 'save');
      },
    });
  }

  /** Une anomalie ne se supprime pas, elle s'archive (RG-anomalie-tracee). */
  onArchive(): void {
    if (!this.anomaly) return;
    if (!this.confirmArchive()) {
      this.confirmArchive.set(true);
      return;
    }
    this.error.set(null);
    this.anomalyService.archive(this.anomaly.id).subscribe({
      next: (anomaly) => this.dialogRef.close(anomaly),
      error: (error) => this.showError(error, 'archive'),
    });
  }

  private showError(error: AnomalyApiError, action: AnomalyAction): void {
    this.error.set(anomalyErrorMessage(error ?? {}, action));
  }
}
